/**
 * PurchaseOrderDetail — drill-in view for a single PO.
 *
 * Sections:
 *  1. Header — PO #, vendor, status pill, customer/job link
 *  2. Vendor & terms — vendor order #, contact, expected delivery, payment terms
 *  3. Lines on this PO (read-only summary; edit happens on the packet)
 *  4. Receipts attached — physical goods received
 *  5. Invoices — vendor bills, payment status, 3-way match status
 *
 * Soft 3-way match: shows variance pill but never blocks marking paid.
 */
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, FileText, DollarSign, CheckCircle2, AlertTriangle, Truck, Receipt, Plus, Trash2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { PO_STATUS_LABEL, PO_STATUS_TONE, type PurchaseOrderStatus, type POInvoiceLink } from "@/data/orderingPacket/types";

const TONE_BADGE: Record<string, string> = {
  muted: "bg-muted text-muted-foreground border-border",
  info: "bg-info/15 text-info border-info/30",
  warning: "bg-warning/15 text-warning border-warning/30",
  success: "bg-success/15 text-success border-success/30",
  destructive: "bg-destructive/15 text-destructive border-destructive/30",
};

interface Props {
  poId: string;
  onClose?: () => void;
}

export function PurchaseOrderDetail({ poId, onClose }: Props) {
  const {
    getPurchaseOrder, updatePOMeta, updatePOStatus,
    addPOInvoice, updatePOInvoice, removePOInvoice,
  } = useOrderingPacket();

  const found = getPurchaseOrder(poId);
  if (!found) {
    return <div className="p-6 text-[12px] text-muted-foreground">Purchase order not found.</div>;
  }
  const { po, packet } = found;
  const tone = PO_STATUS_TONE[po.status];

  const linesOnPO = packet.lineItems.filter((l) => l.poId === po.id);
  const receiptsOnPO = (packet.receipts ?? []).filter((r) => po.receiptIds.includes(r.id));

  const invoiced = po.invoices.reduce((s, i) => s + i.invoiceAmount, 0);
  const unpaid = po.invoices.filter((i) => !i.paid).reduce((s, i) => s + i.invoiceAmount, 0);
  const paid = invoiced - unpaid;
  const variance = po.invoices.length === 0 ? 0 : +(invoiced - po.subtotal).toFixed(2);

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[18px] font-bold">{po.poNumber}</span>
            <Badge className={cn("text-[11px] font-semibold border", TONE_BADGE[tone])}>
              {PO_STATUS_LABEL[po.status]}
            </Badge>
          </div>
          <div className="text-[12px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            <Building2 className="h-3.5 w-3.5" /> <span className="font-medium text-foreground">{po.vendorName}</span>
            <span>·</span>
            <Link to={`/jobs/${po.jobId}`} className="text-primary hover:underline inline-flex items-center gap-1">
              {po.customerName} <ExternalLink className="h-3 w-3" />
            </Link>
            <span className="text-muted-foreground">— {po.jobLabel}</span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            Created {format(new Date(po.createdAt), "MMM d, yyyy h:mm a")} by {po.createdBy}
            {po.sentAt && ` · Sent ${format(new Date(po.sentAt), "MMM d")}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={po.status} onValueChange={(v) => updatePOStatus(packet.id, po.id, v as PurchaseOrderStatus)}>
            <SelectTrigger className="h-8 w-[180px] text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PO_STATUS_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Money grid */}
      <div className="grid grid-cols-4 gap-3">
        <Money label="PO Subtotal" value={po.subtotal} />
        <Money label="Invoiced" value={invoiced} sub={`${po.invoices.length} invoice${po.invoices.length === 1 ? "" : "s"}`} />
        <Money label="Paid" value={paid} tone={paid > 0 ? "success" : undefined} />
        <Money label="Unpaid" value={unpaid} tone={unpaid > 0 ? "warning" : undefined} />
      </div>

      {/* 3-way match banner (soft) */}
      {po.invoices.length > 0 && (
        <div className={cn("rounded-md border px-3 py-2 text-[11px] flex items-center gap-2",
          variance === 0
            ? "bg-success/10 border-success/30 text-success"
            : "bg-warning/10 border-warning/30 text-warning")}>
          {variance === 0 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
          <span className="font-semibold">
            {variance === 0
              ? "3-way match clean — invoice total equals PO subtotal."
              : `3-way variance: invoiced ${variance > 0 ? "+" : ""}$${variance.toLocaleString(undefined, { maximumFractionDigits: 2 })} vs PO subtotal.`}
          </span>
          {variance !== 0 && (
            <span className="text-warning/80 font-normal">
              Soft warning — payment marking is not blocked.
            </span>
          )}
        </div>
      )}

      {/* Vendor & terms */}
      <Card className="p-4 space-y-3">
        <h3 className="text-[12px] font-bold uppercase tracking-wider flex items-center gap-1.5">
          <Truck className="h-3.5 w-3.5" /> Vendor & Terms
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor Order #">
            <Input
              value={po.vendorOrderNumber ?? ""}
              onChange={(e) => updatePOMeta(packet.id, po.id, { vendorOrderNumber: e.target.value })}
              placeholder="e.g. SO-88421"
              className="h-9 text-[12px]"
            />
          </Field>
          <Field label="Vendor Contact">
            <Input
              value={po.vendorContact ?? ""}
              onChange={(e) => updatePOMeta(packet.id, po.id, { vendorContact: e.target.value })}
              placeholder="Name / email / phone"
              className="h-9 text-[12px]"
            />
          </Field>
          <Field label="Expected Delivery">
            <Input
              type="date"
              value={po.expectedDeliveryDate?.slice(0, 10) ?? ""}
              onChange={(e) => updatePOMeta(packet.id, po.id, { expectedDeliveryDate: e.target.value })}
              className="h-9 text-[12px]"
            />
          </Field>
          <Field label="Payment Method">
            <Select
              value={po.paymentMethod ?? ""}
              onValueChange={(v) => updatePOMeta(packet.id, po.id, { paymentMethod: v as typeof po.paymentMethod })}
            >
              <SelectTrigger className="h-9 text-[12px]"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Net 30">Net 30</SelectItem>
                <SelectItem value="Net 15">Net 15</SelectItem>
                <SelectItem value="COD">COD</SelectItem>
                <SelectItem value="Credit Card">Credit Card</SelectItem>
                <SelectItem value="ACH">ACH</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Notes">
          <Textarea
            value={po.notes ?? ""}
            onChange={(e) => updatePOMeta(packet.id, po.id, { notes: e.target.value })}
            className="text-[12px] min-h-[60px]"
            placeholder="Internal notes for this PO…"
          />
        </Field>
      </Card>

      {/* Lines */}
      <Card className="overflow-hidden">
        <div className="p-3 border-b border-border">
          <h3 className="text-[12px] font-bold uppercase tracking-wider flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Lines on this PO ({linesOnPO.length})
          </h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[10px] uppercase tracking-wider w-[110px]">SKU</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Item</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right w-[60px]">Qty</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right w-[100px]">Unit $</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right w-[100px]">Line $</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider w-[100px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {linesOnPO.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-[11px] text-muted-foreground">
                  No lines assigned to this PO.
                </TableCell>
              </TableRow>
            ) : linesOnPO.map((l) => {
              const qty = l.actualQty ?? l.projectedQty;
              const unit = l.actualUnitPrice ?? l.projectedUnitPrice;
              const cost = l.actualLineCost ?? (l.actualUnitPrice != null ? qty * l.actualUnitPrice : l.projectedLineCost);
              return (
                <TableRow key={l.id}>
                  <TableCell className="font-mono text-[11px]">{l.actualSku ?? l.projectedSku}</TableCell>
                  <TableCell className="text-[11px]">{l.actualItemName ?? l.projectedItemName}</TableCell>
                  <TableCell className="text-right tabular-nums text-[11px]">{qty}</TableCell>
                  <TableCell className="text-right tabular-nums text-[11px]">${unit.toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-right tabular-nums text-[11px] font-medium">${cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[9px]">{l.status}</Badge></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Receipts */}
      <Card className="overflow-hidden">
        <div className="p-3 border-b border-border">
          <h3 className="text-[12px] font-bold uppercase tracking-wider flex items-center gap-1.5">
            <Receipt className="h-3.5 w-3.5" /> Vendor Receipts ({receiptsOnPO.length})
          </h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Manage receipts in the packet's <strong>Receipts</strong> tab. Lines auto-link to a PO when assigned to a receipt.
          </p>
        </div>
        {receiptsOnPO.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-wider">Invoice #</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider w-[120px]">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right w-[120px]">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider w-[100px]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {receiptsOnPO.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-[11px]">{r.invoiceNumber || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-[11px]">{r.invoiceDate}</TableCell>
                  <TableCell className="text-right tabular-nums text-[11px] font-medium">${r.invoiceTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                  <TableCell>
                    <Badge className={cn("text-[10px] border", r.locked ? TONE_BADGE.success : TONE_BADGE.muted)}>
                      {r.locked ? "Reconciled" : "Open"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Invoices */}
      <InvoicesSection
        invoices={po.invoices}
        onAdd={(inv) => addPOInvoice(packet.id, po.id, inv)}
        onUpdate={(num, patch) => updatePOInvoice(packet.id, po.id, num, patch)}
        onRemove={(num) => removePOInvoice(packet.id, po.id, num)}
      />

      {onClose && (
        <div className="flex justify-end pt-2">
          <Button size="sm" variant="outline" className="text-[12px]" onClick={onClose}>Close</Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Money({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone?: "warning" | "success" | "destructive" }) {
  const cls = tone === "warning" ? "text-warning" : tone === "success" ? "text-success" : tone === "destructive" ? "text-destructive" : "text-foreground";
  return (
    <Card className="p-3">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
        <DollarSign className="h-3 w-3" />{label}
      </div>
      <div className={cn("text-[16px] font-bold tabular-nums mt-0.5", cls)}>
        ${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </div>
      {sub && <div className="text-[9px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function InvoicesSection({
  invoices, onAdd, onUpdate, onRemove,
}: {
  invoices: POInvoiceLink[];
  onAdd: (inv: POInvoiceLink) => void;
  onUpdate: (num: string, patch: Partial<POInvoiceLink>) => void;
  onRemove: (num: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<POInvoiceLink>({
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().slice(0, 10),
    invoiceAmount: 0,
    paid: false,
  });
  const [paymentRefDraft, setPaymentRefDraft] = useState<Record<string, string>>({});

  const canAdd = draft.invoiceNumber.trim() && draft.invoiceAmount > 0;

  return (
    <Card className="overflow-hidden">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-[12px] font-bold uppercase tracking-wider flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Vendor Invoices ({invoices.length})
          </h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Track each vendor bill. Multiple invoices per PO supported (e.g. deposit + balance).
          </p>
        </div>
        {!adding && (
          <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => setAdding(true)}>
            <Plus className="h-3 w-3 mr-1" /> Add Invoice
          </Button>
        )}
      </div>

      {adding && (
        <div className="p-3 bg-muted/20 border-b border-border space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Field label="Invoice #">
              <Input
                value={draft.invoiceNumber}
                onChange={(e) => setDraft({ ...draft, invoiceNumber: e.target.value })}
                placeholder="e.g. INV-99102"
                className="h-9 text-[12px]"
              />
            </Field>
            <Field label="Invoice Date">
              <Input
                type="date"
                value={draft.invoiceDate}
                onChange={(e) => setDraft({ ...draft, invoiceDate: e.target.value })}
                className="h-9 text-[12px]"
              />
            </Field>
            <Field label="Amount">
              <Input
                type="text"
                inputMode="decimal"
                value={draft.invoiceAmount || ""}
                onChange={(e) => setDraft({ ...draft, invoiceAmount: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                className="h-9 text-[12px] tabular-nums"
              />
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => { setAdding(false); setDraft({ invoiceNumber: "", invoiceDate: new Date().toISOString().slice(0, 10), invoiceAmount: 0, paid: false }); }}>
              Cancel
            </Button>
            <Button size="sm" className="h-8 text-[11px]" disabled={!canAdd} onClick={() => {
              onAdd(draft);
              setAdding(false);
              setDraft({ invoiceNumber: "", invoiceDate: new Date().toISOString().slice(0, 10), invoiceAmount: 0, paid: false });
            }}>
              Attach Invoice
            </Button>
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="p-6 text-center text-[11px] text-muted-foreground">
          No vendor invoices attached yet.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[10px] uppercase tracking-wider">Invoice #</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider w-[110px]">Date</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right w-[120px]">Amount</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider w-[300px]">Payment</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.invoiceNumber}>
                <TableCell className="font-mono text-[11px] font-semibold">{inv.invoiceNumber}</TableCell>
                <TableCell className="text-[11px]">{inv.invoiceDate}</TableCell>
                <TableCell className="text-right tabular-nums text-[11px] font-medium">
                  ${inv.invoiceAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell>
                  {inv.paid ? (
                    <div className="flex items-center gap-2">
                      <Badge className={cn("text-[10px] border", TONE_BADGE.success)}>
                        <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Paid
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {inv.paidDate} · {inv.paymentReference}
                      </span>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => onUpdate(inv.invoiceNumber, { paid: false, paidDate: undefined, paymentReference: undefined })}>
                        Unmark
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <Input
                        placeholder="Check #, ACH ref…"
                        value={paymentRefDraft[inv.invoiceNumber] ?? ""}
                        onChange={(e) => setPaymentRefDraft({ ...paymentRefDraft, [inv.invoiceNumber]: e.target.value })}
                        className="h-7 text-[11px] flex-1"
                      />
                      <Button
                        size="sm"
                        className="h-7 text-[10px] bg-success hover:bg-success/90"
                        onClick={() => onUpdate(inv.invoiceNumber, {
                          paid: true,
                          paidDate: new Date().toISOString().slice(0, 10),
                          paidAmount: inv.invoiceAmount,
                          paymentReference: paymentRefDraft[inv.invoiceNumber] ?? "",
                        })}
                      >
                        Mark Paid
                      </Button>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10" onClick={() => onRemove(inv.invoiceNumber)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
